import { Injectable } from '@angular/core';
import { Observable, interval, Subject, Subscription, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface VersionResponse {
  version: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  // Key for storing version in localStorage
  private storageKey = 'app_version';
  
  // Key for tracking first deployment
  private firstDeployKey = 'app_first_version_check';
  
  // Observable for new version availability
  private newVersionAvailable = new Subject<boolean>();
  public newVersionAvailable$ = this.newVersionAvailable.asObservable();
  
  // Polling interval in milliseconds (5 minutes default)
  private pollInterval = 5 * 60 * 1000;
  private pollingSubscription: Subscription | null = null;
  
  constructor() {}

  /**
   * Initialize the version check service
   * @param pollInterval Optional polling interval in milliseconds
   */
  public initVersionCheck(pollInterval?: number): void {
    if (pollInterval) {
      this.pollInterval = pollInterval;
    }
    
    // Get the initial version from the server
    this.fetchCurrentVersion().subscribe(version => {
      this.recordInitialVersion(version);
      
      // Start polling for version updates
      this.startPolling();
    });
  }

  /**
   * Fetch the current version from the server using Fetch API
   */
  private fetchCurrentVersion(): Observable<string> {
    // Add timestamp to URL to bust any caches
    const timestamp = new Date().getTime();
    const url = `/api/version?t=${timestamp}`;

    const fetchPromise = fetch(url, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      cache: 'no-store'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
      return response.json();
    })
    .then((data: VersionResponse) => data.version)
    .catch(error => {
      console.error('Error fetching version:', error);
      return 'unknown';
    });

    return from(fetchPromise);
  }

  /**
   * Record the initial version when the app first loads
   */
  private recordInitialVersion(version: string): void {
    const storedVersion = localStorage.getItem(this.storageKey);
    const isFirstVersionCheck = localStorage.getItem(this.firstDeployKey) !== 'true';
    
    if (!storedVersion) {
      // First visit - just store the version, no notification
      localStorage.setItem(this.storageKey, version);
      localStorage.setItem(this.firstDeployKey, 'true');
    } else if (storedVersion !== version && !isFirstVersionCheck) {
      // Only show notification if it's not the first time the version check system is used
      // AND the version has changed
      this.newVersionAvailable.next(true);
    } else if (isFirstVersionCheck) {
      // Mark that we've done the first version check now
      localStorage.setItem(this.firstDeployKey, 'true');
      // Update to the current version without notification
      localStorage.setItem(this.storageKey, version);
    }
  }

  /**
   * Start polling for version changes
   */
  private startPolling(): void {
    // Clear any existing subscription
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    
    this.pollingSubscription = interval(this.pollInterval)
      .subscribe(() => {
        this.checkVersion();
      });
  }

  /**
   * Check for a new version using the server API
   */
  public checkVersion(): void {
    this.fetchCurrentVersion().subscribe(latestVersion => {
      const storedVersion = localStorage.getItem(this.storageKey);
      
      // Only show notification if there's a mismatch between stored version and latest
      if (latestVersion && storedVersion && latestVersion !== storedVersion) {
        this.newVersionAvailable.next(true);
      }
    });
  }

  /**
   * Updates the stored version to the latest version from the server
   * We do this when the user clicks "Reload Now"
   */
  public updateStoredVersion(): void {
    this.fetchCurrentVersion().subscribe(version => {
      localStorage.setItem(this.storageKey, version);
      this.newVersionAvailable.next(false);
    });
  }

  /**
   * Reload the application
   */
  public reloadApp(): void {
    this.updateStoredVersion();
    
    window.location.reload();
  }
}